import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css"; // tailwind layers
import "./styles.css"; // legacy styles (will be phased out)
import "./styles/themes.css"; // global navigation theme tokens (--sb-*)
import "./i18n";
import { resolveApiUrl } from "./apiBase";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { detectBuildEnvMismatch, renderBuildEnvErrorScreen } from "./BuildEnvCheck";
import { sparaNuvarandeSomInloggningsmal } from "./utils/postLoginRedirect";

// Dev safety: CRA chunk-load errors can happen if the browser caches old bundles/chunks.
// Auto-reload once to recover, but guard against infinite reload loops.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
	const key = 'kanplan-chunkload-reload-ts';
	const now = Date.now();
	const last = Number(sessionStorage.getItem(key) || '0');
	const recentlyReloaded = Number.isFinite(last) && last > 0 && now - last < 10_000;

	const shouldReload = (msg) => {
		const s = String(msg || '');
		return s.includes('ChunkLoadError') || s.includes('Loading chunk');
	};

	const triggerReload = () => {
		if (recentlyReloaded) return;
		try {
			sessionStorage.setItem(key, String(Date.now()));
		} catch {
			// ignore
		}
		try {
			window.location.reload();
		} catch {
			// ignore
		}
	};

	window.addEventListener('error', (event) => {
		if (shouldReload(event?.message)) triggerReload();
	});
	window.addEventListener('unhandledrejection', (event) => {
		if (shouldReload(event?.reason?.message || event?.reason)) triggerReload();
	});
}

// Dev convenience: avoid Windows/Docker issues where `localhost` can be extremely slow.
// Force the app origin to 127.0.0.1 so cookie SameSite + API host derivation stays consistent.
if (
	process.env.NODE_ENV === 'development' &&
	typeof window !== 'undefined' &&
	window.location &&
	window.location.hostname === 'localhost'
) {
	try {
		const nextUrl = window.location.href.replace('//localhost', '//127.0.0.1');
		if (nextUrl !== window.location.href) {
			window.location.replace(nextUrl);
		}
	} catch {
		// ignore
	}
}

// Global fetch wrapper: include credentials and handle 401 redirects
if (typeof window !== 'undefined' && window.fetch && !window.__kanplanFetchPatched) {
	const apiBase = resolveApiUrl();
	const tenantStorageKey = 'kanplan-active-tenant-slug';
	const debugStorageKey = 'kanplan-debug-network';
	const originalFetch = window.fetch.bind(window);
	window.fetch = async (input, init) => {
		const debugNetwork =
			process.env.NODE_ENV === 'development' &&
			(typeof localStorage !== 'undefined') &&
			(localStorage.getItem(debugStorageKey) === '1');
		const t0 = debugNetwork && typeof performance !== 'undefined' ? performance.now() : 0;
		if (debugNetwork) {
			try {
				window.__kanplanInflightFetches = (window.__kanplanInflightFetches || 0) + 1;
			} catch { /* ignore */ }
		}

		const opts = { ...(init || {}) };
		if (!('credentials' in opts)) opts.credentials = 'include';

		// Attach tenant header for API calls.
		try {
			const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
			const urlStr = String(url || '');
			let isApiCall = false;

			// Absolute calls to the configured API base.
			if (apiBase && urlStr && urlStr.startsWith(String(apiBase))) {
				isApiCall = true;
			}

			// Relative (same-origin) API calls used in parts of the app.
			// We treat any leading-slash URL as safe to attach the tenant header to,
			// since it won't trigger CORS preflight and avoids missing tenant context.
			if (!isApiCall && urlStr && urlStr.startsWith('/')) {
				isApiCall = true;
			}

			const isPublicExternalApiCall = (() => {
				try {
					return (
						urlStr.includes('/management-dashboard/external/') ||
						urlStr.includes('/management-system/external/') ||
						urlStr.includes('/training/external/')
					);
				} catch {
					return false;
				}
			})();

			if (isApiCall && !isPublicExternalApiCall) {
				const slug = (localStorage.getItem(tenantStorageKey) || '').trim();
				if (slug) {
					const h = new Headers(opts.headers || (typeof input !== 'string' && input?.headers ? input.headers : undefined) || {});
					if (!h.has('X-Tenant-Slug')) h.set('X-Tenant-Slug', slug);
					opts.headers = h;
				}
			}
		} catch { /* ignore */ }
		let res;
		try {
			res = await originalFetch(input, opts);
		} catch (err) {
			// passthrough network errors
			if (debugNetwork) {
				try {
					const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
					const dt = typeof performance !== 'undefined' ? (performance.now() - t0) : 0;
					const inflight = window.__kanplanInflightFetches || 0;
					console.warn('[net:error]', String(url || ''), `durMs=${dt.toFixed(1)}`, `inflight=${inflight}`);
				} catch { /* ignore */ }
			}
			if (debugNetwork) {
				try {
					window.__kanplanInflightFetches = Math.max(0, (window.__kanplanInflightFetches || 1) - 1);
				} catch { /* ignore */ }
			}
			throw err;
		}
		if (debugNetwork && res) {
			try {
				const url = typeof input === 'string' ? input : (input && input.url ? input.url : '');
				const finalUrl = res.url || '';
				const redirected = !!res.redirected;
				const dt = typeof performance !== 'undefined' ? (performance.now() - t0) : 0;
				const serverTiming = res.headers?.get?.('server-timing') || '';
				const requestId = res.headers?.get?.('x-request-id') || '';
				const inflight = window.__kanplanInflightFetches || 0;
				console.info(
					'[net]',
					(opts && opts.method) ? opts.method : 'GET',
					String(url || ''),
					finalUrl && finalUrl !== String(url || '') ? `final=${finalUrl}` : '',
					redirected ? 'redirected=1' : '',
					`status=${res.status}`,
					`durMs=${dt.toFixed(1)}`,
					`inflight=${inflight}`,
					requestId ? `x-request-id=${requestId}` : '',
					serverTiming ? `server-timing=${serverTiming}` : ''
				);
			} catch { /* ignore */ }
		}
		if (debugNetwork) {
			try {
				window.__kanplanInflightFetches = Math.max(0, (window.__kanplanInflightFetches || 1) - 1);
			} catch { /* ignore */ }
		}
		if (res && res.status === 401) {
			const requestUrl = (() => {
				try {
					return String(typeof input === 'string' ? input : (input && input.url ? input.url : '') || '');
				} catch {
					return '';
				}
			})();

			const isSkolaApiCall = (() => {
				if (!requestUrl) return false;
				if (requestUrl.startsWith('/api/skola/')) return true;
				if (requestUrl.includes('/api/skola/')) return true;
				return false;
			})();

			const isPublicExternalRoute = (() => {
				try {
					const path = String(window?.location?.pathname || '');
					// Pattern: any path containing a public-route segment.
					// Covers /<module>/ext/<token>, /<module>/external/<token>,
					// /<module>/standalone, /<module>/embed/<token>,
					// /<module>/review/<token> and /<module>/manager/<token>.
					if (/\/(ext|external|standalone|embed|review|manager)(\/|$)/.test(path)) {
						return true;
					}
					// Explicit prefixes that don't fit the pattern above.
					return (
						path.startsWith('/skola') ||
						path.startsWith('/field') ||
						path.startsWith('/equippass') ||
						path.startsWith('/supplier-surveys/') ||
						// Leverantorsportalen for offertforfragan: leverantoren har inget
						// KanPlan-konto och identifieras av token i lanken. Utan raden
						// kastade 401 fran /users/me ut mottagaren till /login innan
						// sidan hann rendera (upptackt i UI-verifiering 2026-09-18).
						path.startsWith('/offert/') ||
						// UE-portalen for saker schakt: UE:s schaktansvarig har inget
						// KanPlan-konto och identifieras av token i lanken. Utan raden
						// kastar 401 fran /users/me ut mottagaren till /login innan
						// sidan hann rendera - samma falla som offertportalen gick i.
						path.startsWith('/schaktrond-ue/') ||
						// Egenkontroller via extern lank (UE utan konto, 2026-10-07).
						path.startsWith('/ek-extern/') ||
						path.startsWith('/survey/') ||
						// Provanställning: svarslänk i påminnelsemejlet, ingen inloggning (2026-10-23).
						path.startsWith('/provanstallning/svar') ||
						path.startsWith('/inskrivning/') ||
						path.startsWith('/accept-invite') ||
						path.startsWith('/reset-password') ||
						// Token-baserade publika svarssidor (revisions + issues —
						// Sprint S2 generisk extern svar-mekanism). Dessa routes
						// är registrerade utanför AuthGuard i App.js men 401-
						// interceptor måste också skippa dem så vi inte tvingar
						// /login-redirect på externa mottagare.
						path.startsWith('/revisions/respond/') ||
						path.startsWith('/issues/respond/') ||
						// Fråga & Svar (RFI): projektören svarar via länk utan konto (2026-10-13).
						path.startsWith('/fraga-svar/respond/') ||
						// Dela handlingar: UE öppnar delade handlingar via länk utan konto.
						path.startsWith('/delat/') ||
						// Beställarvy: beställaren följer kontrollplanen via länk utan konto (2026-10-17).
						path.startsWith('/kontrollplan-extern/') ||
						// KanProfil externa respondenter: /k/invite/{token} öppnas av
						// extern person utan KanPlan-konto. Token-baserad session via
						// X-Profile-Session-header (inte cookie-auth) — 401 fran
						// /users/me eller /tenants/mine far ALDRIG kasta dem till /login.
						path.startsWith('/k/invite/')
					);
				} catch {
					return false;
				}
			})();

			const skipAuthRedirect = (() => {
				try {
					const h = new Headers(opts.headers || (typeof input !== 'string' && input?.headers ? input.headers : undefined) || {});
					const v = String(h.get('X-Kanplan-No-Auth-Redirect') || '').trim();
					return v === '1' || v.toLowerCase() === 'true';
				} catch {
					return false;
				}
			})();

			if (isSkolaApiCall || isPublicExternalRoute || skipAuthRedirect) return res;
			// Kom ihag var anvandaren var pa vag sa inloggningen kan lamna tillbaka
			// dem dit. Filtreringen sitter i hjalparen: inloggningssidans EGEN
			// sessionskontroll ger ocksa 401, och skrev tidigare over det riktiga
			// malet med "/login" — da foll inloggningen tillbaka till startsidan
			// och direktlankar i mejl var i praktiken trasiga.
			sparaNuvarandeSomInloggningsmal();
			if (!window.location.pathname.startsWith('/login')) {
				window.location.href = '/login';
			}
		}
		return res;
	};
	window.__kanplanFetchPatched = true;
}

const container = document.getElementById("root");
if (!container) {
	throw new Error('Root container not found');
}

// Build-env-check: blockerar UI om bundle:n inte matchar host:en (cross-target-deploy-skydd).
// Måste köras INNAN React mountar så användaren aldrig ser den riktiga appen med fel data.
// Källa: lessons.md "2026-05-12 — Stage-bundle på prod"
const _envMismatch = detectBuildEnvMismatch();
if (_envMismatch) {
	renderBuildEnvErrorScreen(_envMismatch);
} else {
	const root = createRoot(container);
	root.render(
		<ErrorBoundary>
			<App />
		</ErrorBoundary>
	);
}

// Legacy training-PWA service worker cleanup.
//
// En tidigare global SW-registrering (kanplan-training-v3) cachade hela
// app.kanplan.se cache-first med hårdkodad CACHE_NAME som aldrig bumpades.
// Det orsakade stage-data-på-prod-incidenten 2026-05-08 där en cachad
// felaktig bundle överlevde efterföljande deploys. SW:n och webmanifest:en
// är borttagna; sw.js är nu en kill-switch som unregister:ar sig själv.
//
// Detta block städar befintliga klienter som redan har den gamla SW:n
// installerad (belt-and-suspenders parallellt med kill-switch i sw.js).
// Kan tas bort efter ~3 månader när alla aktiva browsers har städats.
if ('serviceWorker' in navigator) {
	navigator.serviceWorker.getRegistrations()
		.then((regs) => regs.forEach((r) => r.unregister()))
		.catch(() => {});
	if (typeof caches !== 'undefined') {
		caches.keys()
			.then((keys) => keys.forEach((k) => caches.delete(k)))
			.catch(() => {});
	}
}
